import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import type { ActionResult } from './action-result';

/**
 * Applies a server ActionResult to an RHF form. On failure, field-scoped
 * errors go to their inputs (rendered via the atom's errorMessage, aria wired)
 * and any form-level error goes to `root.serverError` (rendered by
 * FormRootError). The stale root banner is cleared first so a prior server
 * error never lingers across a resubmit. Returns true only on success, so
 * callers gate their success side effects on it.
 */
export function applyActionResult<T extends FieldValues, D = void>(
  form: UseFormReturn<T>,
  result: ActionResult<D>
): boolean {
  form.clearErrors('root.serverError');
  if (result.ok) return true;

  if (result.fieldErrors) {
    for (const [name, message] of Object.entries(result.fieldErrors)) {
      form.setError(name as Path<T>, { type: 'server', message });
    }
  }
  if (result.formError) {
    form.setError('root.serverError', {
      type: 'server',
      message: result.formError,
    });
  }
  return false;
}
